/* eslint-disable indent */
import { useTenant } from '@emerald-works-nova/auth'
import { ImageUtil } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import {
  Close,
  CloseOutlined,
  KeyboardArrowDown,
  Menu,
  Search as SearchIcon
} from '@mui/icons-material'
import { AppBar, Box, Grid, IconButton, Popover, Slide, Toolbar, Tooltip } from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import { useAlert, useGroup, useProducts } from '../../hooks'
import useLogout from '../../hooks/useLogout'
import { HomeOnboarding } from '../../pages/home-enterprise/components/home-onboarding'
import { HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE, useTrackHomeEnterpriseEvent } from '../../pages/home-enterprise/hooks/useTrackHomeEnterpriseEvent'
import { useSearchQuery } from '../../pages/search-results/hooks'
import { contentSlice, newsletterSlice } from '../../reducers'
import { routes } from '../../router/routes'
import { ALL_CONSUMER } from '../../services/cognito/userGroups'
import { ActionButton } from '../action-buttons'
import ConfirmDialog from '../confirm-dialog'
import ExploreMenu from '../explore-menu'
import { ChatAIWhite } from '../icons'
import MTLogo from '../logos/mt-for-business.svg'
import { useMindset } from '../mindset-agent/mindset-context'
import { SubscribeToNewsletterForm } from '../newsletter-form'
import ActionBar from './action-bar'
import { AppbarActionContext } from './context'
import CustomHidden from './hidden'
import useFormFactor from './hooks/useFormFactor'
import MenuButton from './menu-button'
import Search from './search'
import { useStyles } from './style'

const AppToolbar = ({ title }) => {
  const { formFactor, breakpoints } = useFormFactor()
  const newsletterPopoverOpen = useSelector(newsletterSlice.selectors.selectIsNewsletterPopoverOpen)
  const isMeteringEnabled = useSelector(contentSlice.selectors.selectIsMeteringEnabled)
  const isPreview = useSelector(contentSlice.selectors.selectIsPreview)
  const dispatch = useDispatch()
  const classes = useStyles({ bps: breakpoints, isMeteringEnabled })
  const [showExplore, setShowExplore] = useState(false)
  const [showMenu, setShowMenu] = useState(false)
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState()
  const toolbarRef = useRef()
  const tenant = useTenant()
  const { hasContentHubAccess } = useProducts()
  const searchContainerRef = useRef()
  const searchBtnRef = useRef()

  const { isInGroup } = useGroup()
  const [trackNewsletterClosePopover] = useEvent([{ eventName: 'newsletterClosePopover' }])
  const navigate = useNavigate()
  const homeOnboardingAnchorElmRef = useRef(null)
  const { trackOnboardingEvent } = useTrackHomeEnterpriseEvent()

  const { shouldShowMindsetAi, openMindset, isOpen } = useMindset()

  const handleExploreMenuClose = () => setShowExplore(false)

  const { addFilter } = useSearchQuery()

  const { enqueueMsg } = useAlert()

  const { logout } = useLogout()

  const onLogoClick = () => {
    trackOnboardingEvent(HOME_ENTERPRISE_ONBOARDING_EVENT_TYPE.LOGO_CLICKED)
  }

  const handleSearch = (term, from, position) =>
    navigate(addFilter({ search: term }, { routeKey: 'serp', clear: true }), {
      state: { from, position }
    })

  useEffect(() => {
    if (showSuggestions === false && formFactor === 'mobile') {
      searchBtnRef.current?.focus()
    }
  }, [showSuggestions, formFactor])

  const handleLogout = async () => {
    try {
      await logout(routes.login.path)
    } catch (error) {
      enqueueMsg('Unable to logout', 'error')
    }
  }

  const handleCloseNewsletterPopover = useCallback((evt, reason) => {
    dispatch(newsletterSlice.actions.closeNewsletterPopover())
    if (reason) {
      trackNewsletterClosePopover.trigger({
        from: reason
      })
    }
  }, [dispatch, trackNewsletterClosePopover])

  const handleShowMindset = useCallback((evt, source) => {
    openMindset({
      from: source
    })
  }, [openMindset])

  const actionContext = useMemo(() => {
    const getId = ({ props: { id } }) => id.replace(/-action$/, '')
    const handleActionClick = (evt, action) => {
      if (getId(action) === 'explore') {
        handleCloseNewsletterPopover()
        setShowExplore(true)
      } else if (getId(action) === 'logout') {
        handleCloseNewsletterPopover()
        setShowLogoutConfirm(true)
      } else if (getId(action) === 'mindsetai') {
        handleShowMindset(evt, 'navigation')
      }
    }

    const handleMenuShow = open => {
      setShowMenu(open)
    }

    return {
      onClick: handleActionClick,
      onMenuShow: handleMenuShow,
      getId
    }
  }, [handleCloseNewsletterPopover, handleShowMindset])

  const handleNewsletterSuccess = () => {
    setTimeout(() => {
      handleCloseNewsletterPopover()
    }, 4000)
  }

  return (
    <AppbarActionContext.Provider value={actionContext}>
      <Popover
        id='newsletter-popover'
        open={newsletterPopoverOpen}
        onClose={handleCloseNewsletterPopover}
        closeAfterTransition
        anchorReference='anchorPosition'
        classes={{ paper: classes.newsletterPopoverPaper, root: classes.newsletterPopoverRoot }}
        anchorPosition={{ top: 64, left: 0 }}
        TransitionComponent={Slide}
        style={{ zIndex: 1000 }}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <Box display='flex' flexDirection='row' gap='20px'>
          <SubscribeToNewsletterForm
            afterSuccess={handleNewsletterSuccess}
          />
          <IconButton size='small' aria-label='Close newsletter popover' className={classes.closeButton} onClick={(evt) => handleCloseNewsletterPopover(evt, 'closeButton')}>
            <CloseOutlined />
          </IconButton>
        </Box>
      </Popover>
      <AppBar position='static' className={classes.appBar} ref={toolbarRef}>
        <Toolbar className={classes.toolbar} disableGutters>
          <CustomHidden tabletUp>
            <MenuButton
              id='appbar-menu'
              disableActiveState
              button={
                <IconButton
                  aria-label={showExplore || showMenu ? 'Close menu' : 'Open menu'}
                  data-test={`appbar-${showExplore ? 'close' : 'menu'}-button`}
                  size='large'
                >
                  {showExplore || showMenu ? <Close /> : <Menu />}
                </IconButton>
              }
            />
          </CustomHidden>
          <Box ref={homeOnboardingAnchorElmRef}>
            <HomeOnboarding anchorElm={homeOnboardingAnchorElmRef.current} />
            <Tooltip title='Go to Home'>
              <RouterLink to={routes.home.path} onClick={onLogoClick} className={classes.logoWrapper}>
                <img
                  src={
                    tenant.logo?.s3Url
                      ? ImageUtil.ImgixGenerateImageUrl(tenant.logo.s3Url, {
                        h: 40,
                        ...tenant.logo?.imgixCropParams
                      })
                      : MTLogo
                  }
                  height={40}
                  width={200}
                  alt={`${tenant.logo?.s3Url ? tenant.name : 'MindTools'} Home`}
                  className={classes.logo}
                  data-test='appbar-mt-logo'
                />
              </RouterLink>
            </Tooltip>
          </Box>
          {/* Explore Menu Button on Desktop */}
          {hasContentHubAccess &&
            <CustomHidden mobileDown>
              <Grid>
                <ActionButton
                  variant={
                    isInGroup(ALL_CONSUMER) ? 'publicTenantSecondary' : 'primary'
                  }
                  endIcon={<KeyboardArrowDown />}
                  label='Explore'
                  data-test='appbar-expore-button'
                  onClick={() => {
                    setShowExplore(show => !show)
                    handleCloseNewsletterPopover()
                  }}
                />
              </Grid>
            </CustomHidden>}
          {/* Search Bar */}
          {hasContentHubAccess &&
            <Grid className={classes.searchContainer}>
              <Search
                onSearch={handleSearch}
                data-test='appbar-search'
                searchContainerRef={searchContainerRef}
                showSuggestions={showSuggestions}
                onShowSuggestions={setShowSuggestions}
              />
              {formFactor === 'mobile' && !showMenu && (
                <Box className={classes.searchButton}>
                  <IconButton
                    aria-label='Show search'
                    ref={searchBtnRef}
                    onClick={() => setShowSuggestions(true)}
                    size='large'
                  >
                    <SearchIcon />
                  </IconButton>
                </Box>
              )}
            </Grid>}
          <CustomHidden mobileDown>
            <ActionBar />
          </CustomHidden>
        </Toolbar>
      </AppBar>
      {/* Explore Menu */}
      {hasContentHubAccess && <ExploreMenu open={showExplore} onClose={handleExploreMenuClose} />}
      <ConfirmDialog
        name='confirm-logout'
        title='Are you sure you want to logout?'
        open={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
      />
      {(shouldShowMindsetAi && !isPreview && !isOpen) && (
        <>
          <Box
            className={classes.mindsetBubble}
            onClick={(evt) => handleShowMindset(evt, 'bubble')}
            data-test='mindset-ai-bubble'
            style={isMeteringEnabled ? { bottom: '140px' } : {}}
          >
            <ChatAIWhite />
          </Box>
        </>
      )}
    </AppbarActionContext.Provider>
  )
}

export default AppToolbar
